import { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useParams } from 'react-router'

import { ConnectionSetupRoutes } from 'app/application/types'
import { PageHeader } from 'app/common/presentation'
import { useConnections } from 'app/connections/application'
import {
  AvailableOrInstalledConnection,
  ConnectionName,
  isInstalledConnection,
} from 'app/connections/types'

import { Breadcrumb } from 'components'

import { mapToConnectionName, mapToUrl } from '../connectionUrlMapper'
import { translateStatus } from '../translateStatus'
import styles from './Setup.module.scss'

// typescript WA as createContext always needs a param. But we call it always with a valid connection on invocation.
const ConnectionContext = createContext<AvailableOrInstalledConnection>(
  {} as AvailableOrInstalledConnection,
)
// eslint-disable-next-line react-refresh/only-export-components
export const useConnection = () => useContext(ConnectionContext)

type SetupConnectionParams = {
  name: string
}

const SetupConnection = () => {
  const location = useLocation()
  const { name } = useParams<SetupConnectionParams>() as SetupConnectionParams
  const { t } = useTranslation('connections')

  const { getConnection: getConnection } = useConnections()

  const [connection, setConnection] = useState<AvailableOrInstalledConnection>()

  useEffect(() => {
    setConnection(getConnection(mapToConnectionName(name)) ?? undefined)
  }, [getConnection, name])

  const getDescription = (connectionName: ConnectionName) => {
    if (location.pathname.endsWith(ConnectionSetupRoutes.SelectWorkspaces)) {
      return t(`setup.${connectionName}.selectAccounts.description`)
    }
    if (location.pathname.endsWith(ConnectionSetupRoutes.ConnectionVerified)) {
      return undefined
    }

    return t(`setup.${connectionName}.description`)
  }

  const getTitle = (connectionName: ConnectionName) => {
    if (location.pathname.endsWith(ConnectionSetupRoutes.SignIn)) {
      return t(`setup.${connectionName}.signIn`)
    }
    if (location.pathname.endsWith(ConnectionSetupRoutes.SelectWorkspaces)) {
      return t(`setup.${connectionName}.selectAccounts.title`)
    }
    if (location.pathname.endsWith(ConnectionSetupRoutes.ConnectionVerified)) {
      return t(`setup.connectionVerified.title`)
    }
    if (location.pathname.endsWith(ConnectionSetupRoutes.Settings)) {
      return t(`setup.${connectionName}.settings.title`)
    }

    return `${t('setup.headline')} ${t(`available.${connectionName}.displayName`)}`
  }

  return connection ? (
    <div className={styles.setup}>
      <Breadcrumb
        paths={[
          {
            name: t('overview.headline'),
            path: '/connections',
          },
          {
            name: t(`available.${connection.name}.displayName`),
            path: `/connections/${mapToUrl(connection.name)}`,
          },
          {
            name: t('setup.headline'),
          },
        ]}
      />
      <PageHeader
        badge={isInstalledConnection(connection) ? t(`status.${connection.status}`) : undefined}
        badgeClassName={
          isInstalledConnection(connection)
            ? styles[translateStatus(connection.status, connection.error)]
            : undefined
        }
        className={styles.pageHeader}
        description={getDescription(connection.name)}
        docu={t('setup.docuLink')}
        title={getTitle(connection.name)}
      />
      <ConnectionContext.Provider value={connection}>
        <Outlet />
      </ConnectionContext.Provider>
    </div>
  ) : (
    <div>{`Connection ${name} not found`}</div>
  )
}

export default SetupConnection
